import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../wrapper/API';
import { useAuth } from './AuthContext';

const TourContext = createContext();
export const useTour = () => useContext(TourContext);

const LS_KEY = 'ave_tour_completed';

const readFromStorage = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]'); }
  catch { return []; }
};

const writeToStorage = (keys) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(keys)); } catch {}
};

export const TourProvider = ({ children }) => {
  const { isAuth } = useAuth();
  const [completedTours, setCompletedTours] = useState([]);
  const [tourReady, setTourReady] = useState(false);
  const [activeTour, setActiveTour] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [legendOpen, setLegendOpen] = useState(false);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!isAuth) {
      hasLoaded.current = false;
      setTourReady(false);
      setCompletedTours([]);
      return;
    }
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    // Use localStorage immediately — no API round-trip needed before first render
    const stored = readFromStorage();
    setCompletedTours(stored);
    setTourReady(true);

    // Sync with API in background (catches resets from other devices)
    api.get('/Tour/GetStatus')
      .then(res => {
        const apiKeys = res.data ?? [];
        const merged = [...new Set([...stored, ...apiKeys])];
        setCompletedTours(merged);
        writeToStorage(merged);
      })
      .catch(() => {});
  }, [isAuth]);

  const startTour = useCallback((key, steps) => {
    setActiveTour({ key, steps });
    setStepIndex(0);
  }, []);

  const markCompleted = useCallback((key) => {
    api.post('/Tour/MarkCompleted', JSON.stringify(key), {
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {});
    setCompletedTours(prev => {
      const next = prev.includes(key) ? prev : [...prev, key];
      writeToStorage(next);
      return next;
    });
  }, []);

  const resetTour = useCallback((key) => {
    api.post('/Tour/Reset', JSON.stringify(key), {
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {});
    setCompletedTours(prev => {
      const next = prev.filter(k => k !== key);
      writeToStorage(next);
      return next;
    });
  }, []);

  const closeTour = useCallback(() => {
    if (activeTour) markCompleted(activeTour.key);
    setActiveTour(null);
    setStepIndex(0);
  }, [activeTour, markCompleted]);

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    if (stepIndex >= activeTour.steps.length - 1) {
      markCompleted(activeTour.key);
      setActiveTour(null);
      setStepIndex(0);
    } else {
      setStepIndex(i => i + 1);
    }
  }, [activeTour, stepIndex, markCompleted]);

  const prevStep = useCallback(() => {
    setStepIndex(i => Math.max(0, i - 1));
  }, []);

  const isTourCompleted = useCallback((key) => completedTours.includes(key), [completedTours]);
  const openLegend = useCallback(() => setLegendOpen(true), []);
  const closeLegend = useCallback(() => setLegendOpen(false), []);

  return (
    <TourContext.Provider value={{
      activeTour, stepIndex, tourReady,
      startTour, closeTour, nextStep, prevStep,
      isTourCompleted, markCompleted, resetTour,
      legendOpen, openLegend, closeLegend,
    }}>
      {children}
    </TourContext.Provider>
  );
};
